import { useEffect, useRef, useState } from 'react';

import useMemoizedFn from '@/utils/useMemoizedFn';

import { CustomContext } from '@/graphql/client';
import type { OrderAppProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import {
  type ProductCardFragment,
  type ProductFilterInput,
} from '@/graphql/saleor/api';
import { fetchProductBusinessData } from '@/integrations/orderApp';
import { fetchProductCardsData } from '@/integrations/saleor/api';
import {
  CARDS_IMAGE_SIZE,
  CAROUSEL_FETCH_LIMIT,
  NEXT_PUBLIC_IMAGES_FORMAT,
} from '@/lib/const';
import { isAbortLikeError } from '@/lib/errors';
import { mapEdgesToItems } from '@/lib/maps';
import { skusToSlugs } from '@/lib/product';
import type { CtaType } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';

export interface ProductCardFragmentWithVariantCta extends ProductCardFragment {
  launch_date?: number;
  variantCTA?: CtaType;
}

interface UseFetchProductsWithCtaProps {
  excludeSlug?: string;
  existProductsData?: ProductCardFragmentWithVariantCta[];
  fetchLimit?: number;
  filter: ProductFilterInput | undefined;
  orderedSlugs?: string[];
  skip?: boolean; // skip auto fetch
  skipFetchCta?: boolean;
}

export const reorderProductsBySlugs = (
  products: ProductCardFragmentWithVariantCta[],
  orderedSlugs: string[],
): ProductCardFragmentWithVariantCta[] => {
  if (!Array.isArray(orderedSlugs) || orderedSlugs.length === 0) {
    return products;
  }

  return orderedSlugs
    .map((slug) => products.find((product) => product.slug === slug))
    .filter(Boolean) as ProductCardFragmentWithVariantCta[];
};

export const useFetchProductsWithCta = ({
  excludeSlug,
  existProductsData = [],
  fetchLimit = CAROUSEL_FETCH_LIMIT,
  filter,
  orderedSlugs = [],
  skip = false,
  skipFetchCta = false,
}: UseFetchProductsWithCtaProps) => {
  const [products, setProducts] =
    useState<ProductCardFragmentWithVariantCta[]>(existProductsData);
  const { query } = useRegionProvider();
  // Set of SKUs we've already tried to enrich with OrderApp CTA data; prevents
  // the effect from looping when OrderApp has no business data for the SKUs
  // (e.g. preview / pre-launch products).
  const ctaAttemptedSkusRef = useRef<string>('');

  useEffect(() => {
    if (existProductsData && existProductsData.length > 0) {
      setProducts(existProductsData);
      ctaAttemptedSkusRef.current = '';
    }
  }, [existProductsData]);

  const getProducts = useMemoizedFn(async () => {
    // Saleor rejects `first: 0` as missing pagination.
    if (!fetchLimit) {
      return;
    }

    const normalizedFilter = {
      ...filter,
      slugs: skusToSlugs(filter?.slugs ?? []),
    };

    const { data, errors } = await fetchProductCardsData(
      {
        first: fetchLimit,
        thumbnailSize: CARDS_IMAGE_SIZE,
        imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        ...query,
        filter: normalizedFilter,
      },
      CustomContext({
        disableErrorNotification: true,
      }),
    );

    const isDataReturned = !!data?.products?.edges.length;

    if (!errors && isDataReturned) {
      const fetchedProducts = mapEdgesToItems(data?.products);

      // If an excludeSlug is provided, filter it out
      const filteredProducts = excludeSlug
        ? fetchedProducts.filter((product) => product.slug !== excludeSlug)
        : fetchedProducts;

      // Reorder based on the saved SKUs order coming from localstorage
      const orderedProducts = reorderProductsBySlugs(
        filteredProducts,
        orderedSlugs,
      );

      setProducts(orderedProducts);
    }
  });

  useEffect(() => {
    if (products.length || !filter || skip) {
      return;
    }
    getProducts().catch((err: unknown) => {
      if (isAbortLikeError(err)) return;
      console.warn('[useFetchProductsWithCta] getProducts failed:', err);
    });
  }, [products, filter, excludeSlug, skip]);

  useEffect(() => {
    (async () => {
      const isCtaSet = products.some(({ variantCTA }) => !!variantCTA);

      if (!products.length || isCtaSet || skipFetchCta) {
        return;
      }

      const productsSkus = products
        .map((item) => item.defaultVariant?.sku)
        .flatMap((sku) => (sku ? [sku] : []));

      const attemptKey = productsSkus.join('|');
      if (ctaAttemptedSkusRef.current === attemptKey) {
        return;
      }

      try {
        const orderAppData = await fetchProductBusinessData(
          productsSkus,
          query.channel,
        );

        ctaAttemptedSkusRef.current = attemptKey;

        const productsWithVariantCta = getProductsWithVariantCta(
          orderAppData,
          products,
        );

        const orderedProducts = reorderProductsBySlugs(
          productsWithVariantCta,
          orderedSlugs,
        );

        setProducts(orderedProducts);
      } catch (err: unknown) {
        if (isAbortLikeError(err)) return;
        console.warn(
          '[useFetchProductsWithCta] fetchProductBusinessData failed:',
          err,
        );
      }
    })();
  }, [products, skipFetchCta, query.channel]);

  const refetch = async () => {
    if (!filter) {
      return;
    }
    await getProducts().catch((err: unknown) => {
      if (isAbortLikeError(err)) return;
      console.warn('[useFetchProductsWithCta] refetch failed:', err);
    });
  };

  return { products, refetch };
};

export const getProductsWithVariantCta = (
  orderAppData?: OrderAppProductVariantsBusinessDetailsQuery,
  products?: ProductCardFragment[],
): ProductCardFragmentWithVariantCta[] => {
  if (!products?.length) return [];

  // Iterate `products` (not OrderApp variants) so preview / pre-launch SKUs
  // without any OrderApp entry are still returned — just without a CTA.
  const ctaBySku = new Map<string, CtaType | undefined>();
  for (const orderAppItem of orderAppData?.productVariants ?? []) {
    if (orderAppItem?.sku) {
      ctaBySku.set(orderAppItem.sku, orderAppItem.variantCTA ?? undefined);
    }
  }

  return products.map((product) => {
    const sku = product.defaultVariant?.sku;
    return sku && ctaBySku.has(sku)
      ? { ...product, variantCTA: ctaBySku.get(sku) }
      : product;
  });
};
